import { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Accordion, AccordionItem, AccordionBody, AccordionHeader } from 'reactstrap';
import { Link } from 'react-router-dom';
import { getEquips } from '../../shared/equipInfo';
import PageMeta from '../PageMeta';
import '../../App.css';
import PagePagination from '../PagePagination';
import { scrollResultsIntoView } from '../scrollResultsIntoView';
import SkeletonImage from '../SkeletonImage';
import EmptyResults from '../EmptyResults';
import { useDesktopAutofocus } from '../../hooks/useDesktopAutofocus';
import { loadSessionState, saveSessionState } from '../../hooks/useSessionListState';
import SearchInput from '../SearchInput';
import TagFilterPanel from '../tags/TagFilterPanel';
import { entityMatchesSelectedTags } from '../../shared/tags/tagDictionary';

const DEFAULT_EQUIP_FILTERS = {
    type: {
        phys: true,
        mag: true,
        def: true,
        heal: true,
        supp: true
    },
    rarity: {
        one: true,
        two: true,
        three: true,
        four: true,
        five: true,
        six: true
    },
    server: {
        Global: true,
        Unreleased: true
    }
};

const EQUIPS_LIST_STATE_KEY = 'gs-equips-list-state';

const DEFAULT_EQUIPS_LIST_STATE = {
    query: '',
    filters: DEFAULT_EQUIP_FILTERS,
    selectedTags: [],
    tagMatchMode: 'or',
    searchType: 'name',
    sortOrder: 'default',
    sortRarity: 'default',
    equipsPerPage: 100,
    currentPage: 1,
    currentRender: 'card',
};

const Equips = () => {
    //Obtain Equipment Data
    const equips = getEquips();

    const [savedListState] = useState(() =>
        loadSessionState(EQUIPS_LIST_STATE_KEY, DEFAULT_EQUIPS_LIST_STATE)
    );

    //Search Query
    const [query, setQuery] = useState(savedListState.query);

    //Filter Default Settings
    const [filters, setFilters] = useState(savedListState.filters)

    const [selectedTags, setSelectedTags] = useState(savedListState.selectedTags || [])
    const [tagMatchMode, setTagMatchMode] = useState(savedListState.tagMatchMode === 'and' ? 'and' : 'or')

    //Accordion
    const [accordion, setAccordion] = useState("0")
    const toggle = (id) => {
        accordion === id ? setAccordion() : setAccordion(id)
    }
    

    //Function to Set Button Filters
    const handleChange = (event) => {
        const { name, value, checked } = event.target;
        setFilters({
            ...filters,
            [name]: {
            ...filters[name],
            [value]: checked,
            },
        });

        //Set page to 1st on each filter change
        setCurrentPage(1)
    };

    //Sort Options
    const [searchType, setSearchType] = useState(savedListState.searchType)
    const [sortOrder, setSortOrder] = useState(savedListState.sortOrder)
    const [sortRarity, setSortRarity] = useState(savedListState.sortRarity)

    //Default amount of Equips per page
    const [equipsPerPage, setEquipsPerPage] = useState(savedListState.equipsPerPage)
    const [currentPage, setCurrentPage] = useState(savedListState.currentPage)

    //Render Type
    const [currentRender, setCurrentRender] = useState(savedListState.currentRender)

    useEffect(() => {
        saveSessionState(EQUIPS_LIST_STATE_KEY, {
            query,
            filters,
            selectedTags,
            tagMatchMode,
            searchType,
            sortOrder,
            sortRarity,
            equipsPerPage,
            currentPage,
            currentRender,
        });
    }, [query, filters, selectedTags, tagMatchMode, searchType, sortOrder, sortRarity, equipsPerPage, currentPage, currentRender]);

    const clearAdvancedFilters = () => {
        setFilters(DEFAULT_EQUIP_FILTERS)
        setSelectedTags([])
        setTagMatchMode('or')
        setCurrentPage(1)
    }

    const advancedFiltersAtDefault =
        JSON.stringify(filters) === JSON.stringify(DEFAULT_EQUIP_FILTERS)
        && selectedTags.length === 0
        && tagMatchMode === 'or'

    const clearFilters = () => {
        setFilters(DEFAULT_EQUIP_FILTERS)
        setSelectedTags([])
        setTagMatchMode('or')
        setQuery("")
        setSearchType("name")
        setSortOrder("default")
        setSortRarity("default")
        setCurrentPage(1)
    }

    const handleSelectedTagsChange = (next) => {
        setSelectedTags(next)
        setCurrentPage(1)
    }

    const handleTagMatchModeChange = (mode) => {
        setTagMatchMode(mode)
        setCurrentPage(1)
    }

    //Display Equipment Thumbnails
    return (
        <>
            <Container className="gs-page">
                <PageMeta
                    title="Equipment"
                    description="Browse Grand Summoners equipment with stats, skills, passives, and farming details."
                    path="/equips"
                />
                <div className="gs-page-gutter">
                    <header className="gs-page-intro">
                        <h3>Select an Equipment to view the Equipment's information</h3>
                        <p>Please be aware that ALL equips are available, including equips only currently available in Japan.</p>
                    </header>

                    <div className="gs-controls-panel">
                        <RenderSearch query={query} setQuery={setQuery} setCurrentPage={setCurrentPage} searchType={searchType} />
                        <div className="gs-options gs-options--equip">
                            <div className="gs-options__main">
                                <RenderSearchType searchType={searchType} setSearchType={setSearchType} />
                                <RenderSortOptions sortOrder={sortOrder} setSortOrder={setSortOrder} />
                                <RenderSortRarity sortRarity={sortRarity} setSortRarity={setSortRarity} />
                                <RenderShowType currentView={currentRender} onChangeView={setCurrentRender} />
                            </div>
                            <RenderEquipAmount equips={equips} setEquipsPerPage={setEquipsPerPage} equipsPerPage={equipsPerPage} setCurrentPage={setCurrentPage} />
                        </div>
                    </div>

                    <div style={{marginBottom: "var(--gs-space-5)"}}>
                        <Accordion open={accordion} toggle={toggle}>
                            <AccordionItem style={{ borderColor: "var(--gs-border)"}}>
                                <AccordionHeader targetId="1">
                                    <span className="gs-accordion-title">
                                        Advanced Filters
                                    </span>
                                </AccordionHeader>
                                <AccordionBody accordionId="1" style={{ backgroundColor: "var(--gs-bg)", color: "var(--gs-text)", padding: "0.5rem"}}>
                                    <div className="d-flex justify-content-end mb-2">
                                        <button type="button" className="gs-btn gs-btn--danger" onClick={clearAdvancedFilters} disabled={advancedFiltersAtDefault}>
                                            Clear All
                                        </button>
                                    </div>
                                    <Row className="g-2">
                                        <Col xs="12" sm="4">
                                            <ServerFilters filters={filters} handleChange={handleChange} />
                                        </Col>
                                        <Col xs="12" sm="4">
                                            <TypeFilters filters={filters} handleChange={handleChange} />
                                        </Col>
                                        <Col xs="12" sm="4">
                                            <RarityFilters filters={filters} handleChange={handleChange} />
                                        </Col>
                                        <Col xs="12">
                                            <TagFilterPanel
                                                appliesTo="equip"
                                                selectedTags={selectedTags}
                                                onChange={handleSelectedTagsChange}
                                                onClear={() => handleSelectedTagsChange([])}
                                                matchMode={tagMatchMode}
                                                onMatchModeChange={handleTagMatchModeChange}
                                            />
                                        </Col>
                                    </Row>
                                </AccordionBody>
                            </AccordionItem>
                        </Accordion>
                    </div>

                    <RenderEquips
                        equips={equips}
                        filters={filters}
                        selectedTags={selectedTags}
                        tagMatchMode={tagMatchMode}
                        query={query}
                        sortOrder={sortOrder}
                        sortRarity={sortRarity}
                        searchType={searchType}
                        setEquipsPerPage={setEquipsPerPage}
                        equipsPerPage={equipsPerPage}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        currentView={currentRender}
                        onClearFilters={clearFilters}
                    />
                </div>
            </Container>
        </>
    )
}

const RenderEquips = ({ equips, searchType, filters, selectedTags, tagMatchMode, query, sortOrder, sortRarity, equipsPerPage, currentPage, setCurrentPage, currentView, onClearFilters }) => {
    const filteredEquips = equips
        .filter(equip => {
            const isGlobalChecked = filters.server.Global
            const isUnreleasedChecked = filters.server.Unreleased

            //Check for Server
            const loreLower = (equip.lore || '').toLowerCase()
            const hasMatchingServer = (isGlobalChecked && isUnreleasedChecked)
            || ((isGlobalChecked && !isUnreleasedChecked) && !loreLower.includes('currently unreleased in global'))
            || ((!isGlobalChecked && isUnreleasedChecked) && loreLower.includes('currently unreleased in global'))
            
            //Check for Types
            const selectedTypes = Object.keys(filters.type).filter((key) => filters.type[key])
            const hasMatchingTypes = selectedTypes.some((type) => equip.type.includes(`${type}LB`))
            
            //Check for Rarity
            const selectedRarities = Object.keys(filters.rarity).filter((key) => filters.rarity[key])
            const hasMatchingRarities = selectedRarities.some((rarity) => {
                if (rarity === "one" && equip.star === 1) {
                    return true
                }
                if (rarity === "two" && equip.star === 2) {
                    return true
                }
                if (rarity === "three" && equip.star === 3) {
                    return true
                }
                if (rarity === "four" && equip.star === 4) {
                    return true
                }
                if (rarity === "five" && equip.star === 5) {
                    return true
                }
                if (rarity === "six" && equip.star === 6) {
                    return true
                }

                return false
            })
        
        //Check for Search
        const q = query.trim().toLowerCase()
        const hasMatchingTags = entityMatchesSelectedTags(equip.tags, selectedTags, tagMatchMode)
        const isSearchEmpty = q.length === 0
        const hasMatchingName = searchType === 'name'
            ? (isSearchEmpty || equip.name?.toLowerCase().includes(q) || equip.translate?.toLowerCase().includes(q))
            : false
        const hasMatchingSkill = searchType === 'skill'
            ? (isSearchEmpty || equip.skillset?.skill?.toLowerCase().includes(q))
            : false
        const hasMatchingPassive = searchType === 'passive'
            ? (isSearchEmpty || [equip.passive?.ability1, equip.passive?.ability2]
                .filter(a => typeof a === 'string' && a.length > 0)
                .some((ability) => ability.toLowerCase().includes(q)))
            : false

        return hasMatchingServer && hasMatchingTypes && (hasMatchingName || hasMatchingSkill || hasMatchingPassive) && hasMatchingRarities && hasMatchingTags
        })
    .sort((a, b) => {
            //If Type Order checked
            if(sortOrder === "type") 
            {
                const typeOrder = ['/db/Mines/thumbnail/physLB.webp', `/db/Mines/thumbnail/magLB.webp`, `/db/Mines/thumbnail/defLB.webp`, `/db/Mines/thumbnail/healLB.webp`, `/db/Mines/thumbnail/suppLB.webp`]
                return typeOrder.indexOf(a.type) - typeOrder.indexOf(b.type)
            }
            //Default Sort
            return a.id - b.id
        })
    .sort((a, b) => {
        //Rarity Sort Ascending
        const rarityOrder = [6, 5, 4, 3, 2, 1]
        if(sortRarity === "rarityasc") 
        {
            return rarityOrder.indexOf(a.star) - rarityOrder.indexOf(b.star)
        }

        //Rarity Sort Descending
        if(sortRarity === "raritydesc") 
        {
            return rarityOrder.indexOf(b.star) - rarityOrder.indexOf(a.star)
        }

        return 0
    })

    //Determine Page amount
    const totalEquips = filteredEquips.length
    const perPage = Number(equipsPerPage) || 50
    const totalPages = Math.max(1, Math.ceil(totalEquips / perPage))
    const safePage = Math.min(Math.max(1, Number(currentPage) || 1), totalPages)
    const resultsRef = useRef(null)
    const shouldScrollRef = useRef(false)

    const handlePageChange = ( pageNumber ) => {
        shouldScrollRef.current = true
        setCurrentPage(pageNumber)
    }

    useEffect(() => {
        if (!shouldScrollRef.current) {
            return
        }
        shouldScrollRef.current = false
        scrollResultsIntoView(resultsRef.current)
    }, [safePage])

    //Determine what equips to show per page
    const indexOfLastEquip = safePage * perPage
    const indexOfFirstEquip = indexOfLastEquip - perPage
    const equipsToShow = filteredEquips.slice(indexOfFirstEquip, indexOfLastEquip)

    if (totalEquips === 0) {
        return (
            <EmptyResults
                title="No equips match your filters"
                message="Try adjusting search or filters, or clear them to see the full equipment list."
                onClear={onClearFilters}
            />
        )
    }

    //Render Pagination with Equips
    return (
        <div className="gs-results-pane" ref={resultsRef} style={{ marginTop: "var(--gs-space-2)"}}>
            <PagePagination
                currentPage={safePage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                ariaLabel="Equip Pages"
            />

            {
                currentView === "card" ? (
                    <div className="gs-thumb-grid gs-thumb-grid--equip is-paging" key={`card-${safePage}`}>
                    {equipsToShow.map(equip => {
                    //Ensure can display % symbol in URL
                    const encodedTitle = encodeURIComponent(equip.name)
                    if (equip.name === "???") {
                        return null
                    }
                    return (
                        <div className="gs-thumb-card" key={equip.id}>
                            <Link to={`/equips/${encodedTitle}`}>
                                <SkeletonImage
                                    src={equip.image.thumbmax}
                                    alt={equip.name}
                                    frameClassName="gs-skeleton-frame--equip"
                                />
                                <span className="gs-thumb-name">{equip.name}</span>
                            </Link>
                        </div>
                    )
                })}
                    </div>
                ) : (
                    <div>
                    <table className="gs-equip-table is-paging" key={`table-${safePage}`}>
                        <thead>
                            <tr>
                                <th className="gs-equip-table__equip">Equip</th>
                                <th>Active Skill</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                equipsToShow
                                    .filter(equip => equip.name !== "???")
                                    .map(equip => {
                                    const encodedTitle = encodeURIComponent(equip.name)
                                    return (
                                        <tr key={equip.id}>
                                            <td className="gs-equip-table__equip">
                                                <Link to={`/equips/${encodedTitle}`} className="gs-equip-table__item">
                                                    <SkeletonImage
                                                        src={equip.image.thumbmax}
                                                        alt={equip.name}
                                                        frameClassName="gs-skeleton-frame--equip-table"
                                                    />
                                                    <span className="gs-equip-table__name">{equip.name}</span>
                                                </Link>
                                            </td>
                                            <td className="gs-equip-table__desc">
                                                {equip.skillset.skill}
                                            </td>
                                        </tr>
                                    )
                                })
                            }
                        </tbody>
                    </table>
                    </div>
                )
                
            }
            <div style={{ marginTop: "var(--gs-space-4)"}}>
                <PagePagination
                    currentPage={safePage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    ariaLabel="Equip Pages"
                    showMeta={false}
                />
            </div>
        </div>
    )
}

const RenderSearch = ({ query, setQuery, setCurrentPage, searchType }) => {
    const searchRef = useDesktopAutofocus();

    const handleSearch = (e) => {
        setQuery(e.target.value)
        setCurrentPage(1)
    }

    return (
        <SearchInput
            inputRef={searchRef}
            placeholder={
                searchType === "skill"
                    ? "Search Equipment Skillset"
                    : searchType === "passive"
                        ? "Search Equipment Passive"
                        : "Search Equipment Name"
            }
            value={query}
            onChange={handleSearch}
            onClear={() => {
                setQuery("")
                setCurrentPage(1)
            }}
        />
    )
}

const RenderSearchType = ({ searchType, setSearchType }) => {
    return (
        <div className="gs-field">
            <span className="gs-field-label">Search By</span>
            <div className="gs-seg gs-seg--triple">
                <label className={searchType === "name" ? "is-active" : ""}>
                    <input type="radio" value="name" checked={ searchType === "name" } onChange={() => setSearchType("name")} />
                    Name
                </label>
                <label className={searchType === "skill" ? "is-active" : ""}>
                    <input type="radio" value="skill" checked={ searchType === "skill" } onChange={() => setSearchType("skill")} />
                    Skillset
                </label>
                <label className={searchType === "passive" ? "is-active" : ""}>
                    <input type="radio" value="passive" checked={ searchType === "passive" } onChange={() => setSearchType("passive")} />
                    Passive
                </label>
            </div>
        </div>
    )
}

const RenderSortOptions = ({ sortOrder, setSortOrder }) => {
    return (
        <div className="gs-field">
            <span className="gs-field-label">Sort By</span>
            <div className="gs-seg">
                <label className={sortOrder === "default" ? "is-active" : ""}>
                    <input type="radio" value="default" checked={ sortOrder === "default" } onChange={() => setSortOrder("default")} />
                    ID
                </label>
                <label className={sortOrder === "type" ? "is-active" : ""}>
                    <input type="radio" value="type" checked={ sortOrder === "type" } onChange={() => setSortOrder("type")} />
                    Type
                </label>
            </div>
        </div>
    )
}

const RenderShowType = ({ currentView, onChangeView }) => {
    return (
        <div className="gs-field">
            <span className="gs-field-label">View</span>
            <div className="gs-seg">
                <label className={currentView === "card" ? "is-active" : ""}>
                    <input type="radio" value="default" checked={ currentView === "card" } onChange={() => onChangeView("card")} />
                    Card
                </label>
                <label className={currentView === "type" ? "is-active" : ""}>
                    <input type="radio" value="type" checked={ currentView === "type" } onChange={() => onChangeView("type")} />
                    Table
                </label>
            </div>
        </div>
    )
}

const RenderSortRarity = ({ sortRarity, setSortRarity }) => {
    return (
        <div className="gs-field">
            <span className="gs-field-label">Rarity</span>
            <div className="gs-seg">
                <label className={sortRarity === "default" ? "is-active" : ""}>
                    <input type="radio" value="default" checked={ sortRarity === "default" } onChange={() => setSortRarity("default")} />
                    Default
                </label>
                <label className={sortRarity === "rarityasc" ? "is-active" : ""}>
                    <input type="radio" value="rarityasc" checked={ sortRarity === "rarityasc" } onChange={() => setSortRarity("rarityasc")} />
                    Desc.
                </label>
                <label className={sortRarity === "raritydesc" ? "is-active" : ""}>
                    <input type="radio" value="raritydesc" checked={ sortRarity === "raritydesc" } onChange={() => setSortRarity("raritydesc")} />
                    Asc.
                </label>
            </div>
        </div>
    )
}
const RenderEquipAmount = ({ setEquipsPerPage, equipsPerPage, equips, setCurrentPage }) => {
    const handleSelectChange = (e) => {
        setEquipsPerPage(e.target.value)
        setCurrentPage(1)
    }

    return (
        <div className="gs-field gs-field--show">
            <span className="gs-field-label">Show</span>
            <select className="gs-select" value={equipsPerPage} onChange={handleSelectChange}>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="200">200</option>
                <option value={equips.length}>All</option>
            </select>
        </div>
    )
}

const ServerFilters = ({ filters, handleChange }) => {
    return (
        <div className="gs-filter-group">
            <span className="gs-field-label">Server</span>
            <form className="filterButtons">
                <label className={filters.server.Global ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="server"
                        value="Global"
                        checked={filters.server.Global}
                        onChange={handleChange}
                        />
                        Global
                </label>
                <label className={filters.server.Unreleased ? "gs-chip is-active" : "gs-chip"}>
                    <input
                        type="checkbox"
                        name="server"
                        value="Unreleased"
                        checked={filters.server.Unreleased}
                        onChange={handleChange}
                        />
                        Unreleased
                </label>
            </form>
        </div>
    )
}

const TypeFilters = ({ filters, handleChange }) => {
    return (
        <div className="gs-filter-group">
            <span className="gs-field-label">Type</span>
            <form className="filterButtons">
                <label className={filters.type.phys ? "gs-chip is-active" : "gs-chip"} >
                    <input
                        type="checkbox"
                        name="type"
                        value="phys"
                        checked={filters.type.phys}
                        onChange={handleChange}
                        />
                    <img src="/db/Mines/thumbnail/physLB.webp" alt="Physical Equipment" className="filterIcons" />
                    Physical
                </label>
                <label className={filters.type.mag ? "gs-chip is-active" : "gs-chip"} >
                    <input
                        type="checkbox"
                        name="type"
                        value="mag"
                        checked={filters.type.mag}
                        onChange={handleChange}
                        />
                    <img src="/db/Mines/thumbnail/magLB.webp" alt="Magical Equipment" className="filterIcons" />
                    Magical
                </label>
                <label className={filters.type.def ? "gs-chip is-active" : "gs-chip"} >
                    <input
                        type="checkbox"
                        name="type"
                        value="def"
                        checked={filters.type.def}
                        onChange={handleChange}
                        />
                    <img src="/db/Mines/thumbnail/defLB.webp" alt="Defense Equipment" className="filterIcons" />
                    Defense
                </label>
                <label className={filters.type.heal ? "gs-chip is-active" : "gs-chip"} >
                    <input
                        type="checkbox"
                        name="type"
                        value="heal"
                        checked={filters.type.heal}
                        onChange={handleChange}
                        />
                    <img src="/db/Mines/thumbnail/healLB.webp" alt="Healing Equipment" className="filterIcons" />
                    Healing
                </label>
                <label className={filters.type.supp ? "gs-chip is-active" : "gs-chip"} >
                    <input
                        type="checkbox"
                        name="type"
                        value="supp"
                        checked={filters.type.supp}
                        onChange={handleChange}
                        />
                    <img src="/db/Mines/thumbnail/suppLB.webp" alt="Support Equipment" className="filterIcons" />
                    Support
                </label>
            </form>
        </div>
    )
}

const RarityFilters = ({ filters, handleChange }) => {
    return (
        <div className="gs-filter-group">
            <span className="gs-field-label">Rarity</span>
            <form className="filterButtons">
                <label className={`gs-chip rarityLabel${filters.rarity.one ? " is-active" : ""}`}>
                    <input
                        type="checkbox"
                        name="rarity"
                        value="one"
                        checked={filters.rarity.one}
                        onChange={handleChange} 
                        />
                    <img src="/db/Icons/other/equip1_star.webp" alt="One Star Rarity" />
                </label>
                <label className={`gs-chip rarityLabel${filters.rarity.two ? " is-active" : ""}`}>
                    <input
                        type="checkbox"
                        name="rarity"
                        value="two"
                        checked={filters.rarity.two}
                        onChange={handleChange} 
                        />
                    <img src="/db/Icons/other/equip2_star.webp" alt="Two Star Rarity" />
                </label>
                <label className={`gs-chip rarityLabel${filters.rarity.three ? " is-active" : ""}`}>
                    <input
                        type="checkbox"
                        name="rarity"
                        value="three"
                        checked={filters.rarity.three}
                        onChange={handleChange} 
                        />
                    <img src="/db/Icons/other/equip3_star.webp" alt="Three Star Rarity" />
                </label>
                <label className={`gs-chip rarityLabel${filters.rarity.four ? " is-active" : ""}`}>
                    <input
                        type="checkbox"
                        name="rarity"
                        value="four"
                        checked={filters.rarity.four}
                        onChange={handleChange} 
                        />
                    <img src="/db/Icons/other/equip4_star.webp" alt="Four Star Rarity" />
                </label>
                <label className={`gs-chip rarityLabel${filters.rarity.five ? " is-active" : ""}`}>
                    <input
                        type="checkbox"
                        name="rarity"
                        value="five"
                        checked={filters.rarity.five}
                        onChange={handleChange} 
                        />
                    <img src="/db/Icons/other/equip5_star.webp" alt="Five Star Rarity" />
                </label>
                <label className={`gs-chip rarityLabel${filters.rarity.six ? " is-active" : ""}`}>
                    <input
                        type="checkbox"
                        name="rarity"
                        value="six"
                        checked={filters.rarity.six}
                        onChange={handleChange} 
                        />
                    <img src="/db/Icons/other/equip6_star.webp" alt="Six Star Rarity" />
                </label>
            </form>
        </div>
    )
}
export default Equips;